/**
 * Is the route the visitor asked for a public experience?
 *
 * The auth guard sends anyone without an edge session to the login page. That
 * is wrong for a public experience, which is meant to serve readers who never
 * sign in: they are issued an anonymous session once the portal's appearance
 * loads. Bouncing them first means they never get that far — the login page
 * sends them back to the portal, which bounces them to the login page again.
 *
 * The guard runs before the portal's appearance (and so before its isPrivate
 * flag) is known, so publicness is read from the alias list, which reports it
 * per portal and is the same source the login page uses.
 */

import axios from "axios";
import { get_DASHBOARD_URI_BASE } from "../../configs/env";

export interface AliasEntry {
  workspaceId: string;
  workspaceAlias: string;
  portalAlias: string;
  isPublic?: boolean;
}

/**
 * Whether the named experience is public, according to the alias list.
 *
 * Unknown routes are NOT public: the guard's job is to protect what needs
 * protecting, so anything it cannot identify keeps the login it would have had.
 */
export const isPublicExperience = (
  aliases: AliasEntry[],
  workspaceAlias: string | undefined,
  portalAlias: string | undefined,
): boolean => {
  if (!workspaceAlias || !portalAlias) return false;
  return aliases.some(
    (a) =>
      a.workspaceAlias === workspaceAlias &&
      a.portalAlias === portalAlias &&
      a.isPublic === true,
  );
};

// The guard runs on every route change, and the alias list is the same for all
// of them, so fetch it once per page load rather than per navigation.
let aliasListPromise: Promise<AliasEntry[]> | null = null;

export const fetchAliasList = (): Promise<AliasEntry[]> => {
  if (!aliasListPromise) {
    aliasListPromise = axios
      .get(`${get_DASHBOARD_URI_BASE()}/alias-list-by-name/_`)
      .then((res) => res?.data?.data ?? [])
      .catch(() => {
        // Don't cache a failure: the next navigation should try again rather
        // than treat a blip as "nothing is public" for the rest of the session.
        aliasListPromise = null;
        return [];
      });
  }
  return aliasListPromise;
};

/** Convenience wrapper: fetch the alias list and answer for one route. */
export const routeIsPublicExperience = async (
  workspaceAlias: string | undefined,
  portalAlias: string | undefined,
): Promise<boolean> => {
  if (!workspaceAlias || !portalAlias) return false;
  return isPublicExperience(
    await fetchAliasList(),
    workspaceAlias,
    portalAlias,
  );
};
